import { DOCUMENT } from '@angular/common';
import { Inject, Injectable, NgZone } from '@angular/core';
import { WebsiteSettings } from '@core/interfaces/website-settings.interface';
import { GlobalService, MediaQuery } from '@core/services/common/global.service';
import { MediaQueryBuilder } from '@core/services/common/styles/media-query-builder';
import { PSEUDO_BACKGROUND_IMAGE_VAR } from '@core/services/common/styles/style-builder';
import { getImgSizeByContainerSize } from '@shared/helpers/image-url-helpers';
import { first } from 'rxjs/operators';

const blockNameSection = 'blocks';
const pageNameSection = 'page';

interface BgItem {
  mediaQuery: string;
  modelId: string | null;
  uId: string | null;
  backgroundImage: string;
  backgroundStyles: Record<string, string>;
  isHover: boolean;
  // Bloki z animacją tła malują je na ::before, do którego nie da się zapisać stylu inline —
  // adres obrazu jedzie wtedy przez zmienną CSS ustawianą na samym bloku.
  backgroundImageProperty: string;
}

@Injectable({providedIn: 'root'})
export class StyleService {
  static bodyStyles: string[] = [];
  static pageStyles: string[] = [];
  public bgItemsBody: BgItem[] = [];
  private lightboxViewContainerWidth = 0;

  constructor(
    private globalService: GlobalService,
    @Inject(DOCUMENT) private document: Document,
    private ngZone: NgZone,
  ) {
    this.globalService.mediaQuery.subscribe((mq) => {
      this.addBackgroundImage(mq);
    });
  }

  static reloadPageStyles(pageRootStyles: string): void {
    this.pageStyles = [];
    this.pageStyles.push(pageRootStyles);
  }

  setPageWidthFromWebsiteSettings(
    websiteSettings: WebsiteSettings,
    tagName = 'app-main-page',
  ): string {
    if (!websiteSettings) {
      return '';
    }

    const basicStyle = MediaQueryBuilder.buildRoot(websiteSettings, tagName);

    StyleService.bodyStyles.push(basicStyle);
  }

  setFooterStyleFromWebsiteSettings(websiteSettings: WebsiteSettings): string {
    if (!websiteSettings) {
      return '';
    }
    const data = {
      styles: {},
    };
    let {styles} = websiteSettings;
    if (!styles) {
      styles = {};
    }

    Object.keys(styles).forEach((key) => {
      if (Object.keys(styles[key]).length) {
        const {padding = '0px'} = styles[key].basic;
        data.styles[key] = {basic: {padding}};
      }
    });

    const basicStyle = MediaQueryBuilder.buildRoot(data, 'footer.fixed');

    StyleService.bodyStyles.push(basicStyle);
  }

  setStyles() {
    const head = this.document.getElementsByTagName('head')[0];

    // 1-5. ISTNIEJĄCY KOD POZOSTAJE BEZ ZMIAN
    StyleService.bodyStyles = [...new Set(StyleService.bodyStyles)];
    let bodyStylesString = '';
    const bgRegex = /\.block_([A-Za-z0-9_-]+)(?:\.m(\d+))?(?::(hover))?\s*{([^}]*)\}/g;
    const bodyBgItems: BgItem[] = [];

    StyleService.bodyStyles.forEach((styleString: string) => {
      const mq = styleString.split('@media ');
      mq.filter(v => !!v.trim()).map(q => `@media ${q}`).forEach((mqString: string) => {
        const mediaQuery = mqString.split('{')[0].replace('@media ', '').trim();
        bodyStylesString += mqString.replace(
          bgRegex,
          (fullMatch, uId, modelId, hover, declBlock) => {
            if (!/background-image\s*:\s*url\(/.test(declBlock)) {
              return fullMatch;
            }
            const isHover = hover === 'hover';
            const decls = declBlock
              .split(';')
              .map(d => d.trim())
              .filter(d => d);

            let bgUrl = '';
            let backgroundImageProperty = 'background-image';
            const bgStyles: Record<string, string> = {};
            decls.forEach(decl => {
              const [prop, ...vals] = decl.split(':');
              const name = prop.trim();
              const value = vals.join(':').trim();
              if (name === 'background-image' || name === PSEUDO_BACKGROUND_IMAGE_VAR) {
                const m = value.match(/url\(\s*["']?(.*?)["']?\s*\)/);
                if (m) {
                  bgUrl = m[1];
                  backgroundImageProperty = name;
                }
              } else if (name.startsWith('background')) {
                bgStyles[name] = value;
              }
            });

            if (bgUrl) {
              bodyBgItems.push({
                mediaQuery, modelId, uId, backgroundImage: bgUrl, backgroundStyles: bgStyles, isHover,
                backgroundImageProperty,
              });
            }

            const filtered = decls
              .filter(d => {
                const p = d.split(':')[0].trim();
                return !p.startsWith('background') && p !== PSEUDO_BACKGROUND_IMAGE_VAR;
              })
              .join(';');
            const modelSuffix = modelId ? `.m${modelId}` : '';
            const filterSuffix = filtered ? `{${filtered}}` : '';

            return `.block_${uId}${modelSuffix}${filterSuffix}`;
          },
        );
      });
    });

    this.bgItemsBody = bodyBgItems;
    this.removeStyleSection(head, blockNameSection);
    const bodyStyle = this.creteStyleSection(blockNameSection, bodyStylesString);
    head.appendChild(bodyStyle);

    this.removeStyleSection(head, pageNameSection);
    let pageStylesString = '';
    StyleService.pageStyles.forEach((mq: string) => (pageStylesString += mq));
    StyleService.pageStyles = [...new Set(StyleService.pageStyles)];
    const pageStyle = this.creteStyleSection(pageNameSection, pageStylesString);
    head.appendChild(pageStyle);

    const mq = this.globalService.mediaQuery.value;
    this.ngZone.onStable.pipe(first()).subscribe(() => {
      this.addBackgroundImage(mq);
      this.addResponsiveImages();
    });
  }

  private addResponsiveImages(): void {
    // console.log('[addResponsiveImages] Rozpoczynam przetwarzanie responsywnych obrazków');

    const imgs = this.document.querySelectorAll('img[data-src]:not([src])') as NodeListOf<HTMLImageElement>;

    imgs.forEach(img => {
      const dataSrc = img.getAttribute('data-src');
      const container = img.parentElement;

      // console.log(
      //   '[addResponsiveImages] Przetwarzanie obrazka',
      //   {
      //     index: index + 1,
      //     total: imgs.length,
      //     dataSrc,
      //     container,
      //     img
      //   }
      // );

      if (!dataSrc) {
        // console.log('[addResponsiveImages] Brak data-src – pomijam obrazek', { index: index + 1 });
        return;
      }

      if (!container) {
        // console.log('[addResponsiveImages] Brak kontenera – pomijam obrazek', { index: index + 1, dataSrc });
        return;
      }

      this.measureWithProgression(container, dataSrc, img);
    });

    // console.log('[addResponsiveImages] Zakończono przetwarzanie wszystkich obrazków');
  }

  public measureWithProgression(container: HTMLElement, dataSrc: string, img: HTMLImageElement): void {

    // Jeśli obraz znajduje się w lightboxie i nie jest częścią .photo
    if (container.closest('app-lightbox') && !img.closest('.photo')) {
      // Używamy pełnego URL-a jako początkowego źródła
      const finalUrl = dataSrc;
      // Tworzymy obiekt URL, aby łatwo odczytać parametr 'size'
      const urlObj = new URL(finalUrl, this.document.baseURI);
      const sizeParam = urlObj.searchParams.get('size');

      // Jeśli parametr 'size' istnieje, porównujemy go z szerokością kontenera.
      if (sizeParam) {
        const imageMaxSize = parseInt(sizeParam, 10) || 0;
        const containerWidth = Math.round(container.getBoundingClientRect().width) || 0;

        // Jeśli kontener jest mniejszy lub równy maksymalnej szerokości obrazka,
        // ładujemy obraz od razu i przerywamy dalszą logikę
        if (containerWidth <= imageMaxSize) {
          img.src = finalUrl;
          return;
        }
        // W przeciwnym wypadku kontener jest większy — pomijamy return,
        // by przejść do progresywnego ładowania (aby pobrać większy obraz)
      } else {
        // Jeśli nie ma parametru 'size', uznajemy URL za pełny obraz i ładujemy go
        img.src = finalUrl;
        return;
      }
    }

    // Zmienne pomocnicze do stabilnego pomiaru szerokości
    let attempts = 0;
    let lastWidth = 0;
    const maxAttempts = 5;

    const attemptMeasurement = () => {
      attempts++;

      // Pierwszy pomiar szerokości kontenera przy użyciu getBoundingClientRect
      let width = Math.round(container.getBoundingClientRect().width);

      // Jeśli wynik nie jest wiarygodny, próbujemy kolejnych metod
      if (width <= 0 || width > window.innerWidth * 2) {
        width = container.offsetWidth;
      }
      if (width <= 0) {
        width = container.clientWidth;
      }

      // Jeśli to pierwsza próba, zapisujemy szerokość i czekamy na stabilizację
      if (attempts === 1) {
        lastWidth = width;
        requestAnimationFrame(() => requestAnimationFrame(attemptMeasurement));
        return;
      }

      // Jeśli szerokość zmieniła się znacząco (>10px), powtarzamy pomiar
      if (Math.abs(width - lastWidth) > 10 && attempts < maxAttempts) {
        lastWidth = width;
        requestAnimationFrame(() => requestAnimationFrame(attemptMeasurement));
        return;
      }

      // Cache szerokości dotyczy WYŁĄCZNIE widoku podstawowego lightboxa
      // (app-lightbox-basic-view), gdzie wszystkie miniatury mają tę samą szerokość.
      // Widok powiększony (app-lightbox-preview-view) jest szerszy i przy starcie ukryty
      // (szerokość 0) — nie może współdzielić ani zanieczyszczać tego cache, bo dobrałby
      // dla siebie miniaturę o rozmiarze widoku podstawowego.
      if (container.closest('app-lightbox-basic-view') && !this.lightboxViewContainerWidth) {
        this.lightboxViewContainerWidth = width;
      } else if (container.closest('app-lightbox-basic-view') && this.lightboxViewContainerWidth) {
        // Przy kolejnych pomiarach używamy zapamiętanej szerokości
        width = this.lightboxViewContainerWidth;
      }

      // Jeśli mamy stabilną szerokość, wywołujemy właściwe ładowanie obrazu
      if (width > 0) {
        this.applyImageWithSize(img, dataSrc, width);
      }
    };

    // Rozpocznij cykl pomiarów w kolejnych klatkach animacji
    requestAnimationFrame(attemptMeasurement);
  }

  private applyImageWithSize(img: HTMLImageElement, dataSrc: string, width: number): void {
    const baseUrl = dataSrc.split('?')[0];
    img.src = this.getImageUrl(baseUrl, width.toString());
    // console.log('Progressive measurement - dataSrc, finalWidth, newUrl:', dataSrc, width, newUrl);
  }

  private getBackgroundsForQuery(mq: MediaQuery): BgItem[] {
    const mediaQuery = mq.breakpoint;
    const priority = [GlobalService.desktopQuery, GlobalService.tabletQuery, GlobalService.mobileQuery];
    const targetIndex = priority.indexOf(mediaQuery);
    if (targetIndex === -1) {
      return [];
    }

    // wybieramy tylko te mediaQueries, które mieszczą się w aktualnym zakresie
    const allowedQueries = priority.slice(0, targetIndex + 1);

    // filtrujemy i odwracamy priorytet — by niższe breakpointy nadpisywały wyższe
    const filtered = this.bgItemsBody.filter(i => allowedQueries.includes(i.mediaQuery)).reverse();

    // usuwamy duplikaty po uId — zostawiamy pierwsze wystąpienie (czyli z niższego breakpointa)
    const unique = [];
    const seen = new Set();

    for (const item of filtered) {
      const val = `${item.uId}${item.isHover ? ':hover' : ''}`;
      if (!seen.has(val)) {
        seen.add(val);
        unique.push(item);
      }
    }

    // odwracamy z powrotem, żeby zachować oryginalną kolejność
    return unique.reverse();
  }

  private addBackgroundImage(mq: MediaQuery): void {
    const filteredList = this.getBackgroundsForQuery(mq);
    filteredList
      .filter(item => item.uId || item.modelId)
      .filter(item => !item.isHover)
      .forEach(item => {
        const selectors = [
          `.block_${item.uId}`,
          `.block_${item.modelId}`,
        ].join(', ');

        const els = this.document.querySelectorAll<HTMLElement>(selectors);

        if (!els.length) {
          // console.warn(`Element nie znaleziony: ${selectors}`);
          return;
        }

        // Ten sam uId/modelId może wystąpić w wielu miejscach w DOM naraz
        // (np. klony domykające karuzelę w lightboxie), więc trzeba obsłużyć wszystkie wystąpienia.
        els.forEach(el => {
          // 1. nanieś zapisane wcześniej style background-*
          Object.entries(item.backgroundStyles).forEach(([prop, value]: [string, string]) => {
            el.style.setProperty(prop, value);
          });

          let width = Math.round(el.getBoundingClientRect().width);
          // Cache szerokości dotyczy WYŁĄCZNIE widoku podstawowego lightboxa
          // (patrz komentarz w measureWithProgression). Elementy widoku powiększonego
          // liczą własną szerokość i nie korzystają z tego cache.
          if (el.closest('app-lightbox-basic-view') && !this.lightboxViewContainerWidth) {
            this.lightboxViewContainerWidth = width;
          } else if (el.closest('app-lightbox-basic-view') && this.lightboxViewContainerWidth) {
            // Przy kolejnych pomiarach używamy zapamiętanej szerokości
            width = this.lightboxViewContainerWidth;
          }
          // 2. wygeneruj nowy URL i przypisz go inline z !important
          const baseUrl = item.backgroundImage.split('?')[0];
          const newUrl = this.getImageUrl(baseUrl, width.toString());
          item.backgroundImage = newUrl;
          StyleService.setBackgroundImage(el, item.backgroundImageProperty, newUrl);
        });
      });
    // elementy ktore byly aktywne a juz nie są i na active mialy background image a juz nie mają
    filteredList
      .filter(item => item.uId?.endsWith('__active'))
      .forEach(item => {
        const noActiveClass = item.uId.replace('__active', '');
        const selectorActive = `block_${item.uId}`;
        const selector = `.block_${noActiveClass}`;
        const els = this.document.querySelectorAll(selector) as NodeListOf<HTMLElement>;
        if (!els.length) {
          // console.warn(`Element nie znaleziony: ${selector}`);
          return;
        }

        els.forEach(el => {
          if (!el.classList.contains(selectorActive)) {
            el.style.removeProperty(item.backgroundImageProperty);
            Object.keys(item.backgroundStyles).forEach((prop) => {
              el.style.removeProperty(prop);
            });
          }
        });
      });

    filteredList
      .filter(item => item.uId && item.isHover)
      .map(item => {
        const data = {
          uId: item.uId,
          modelId: item.modelId,
          hoverBg: item.backgroundImage,
          standardBg: item.backgroundImage,
        };
        const standardData = filteredList.find(i => i.uId === item.uId && !i.isHover);
        if (standardData) {
          data.standardBg = standardData.backgroundImage;
        }
        return data;
      })
      .filter(item => item.hoverBg !== item.standardBg)
      .forEach(item => {
        const selectors = [
          `.block_${item.uId}`,
          `.block_${item.modelId}`,
        ].join(', ');

        const els = this.document.querySelectorAll<HTMLElement>(selectors);

        if (!els.length) {
          // console.warn(`Element nie znaleziony: ${selectors}`);
          return;
        }

        els.forEach(el => {
          if (el.dataset.bgHoverHandlersAttached === '1') {
            return;
          }
          el.dataset.bgHoverHandlersAttached = '1';

          el.addEventListener('mouseover', () => {
            el.style.backgroundImage = `url(${item.hoverBg})`;
          });
          el.addEventListener('mouseout', () => {
            el.style.backgroundImage = `url(${item.standardBg})`;
          });
        });
      });
  }

  /**
   * Przelicza tła (background-image) elementów widoku powiększonego lightboxa
   * przy ich RZECZYWISTEJ szerokości. Wołane po otwarciu podglądu, gdy jest on już
   * widoczny. Powód: przy starcie strony widok powiększony jest ukryty (display:none,
   * szerokość 0), więc addBackgroundImage dobrałby dla niego obraz w rozmiarze miniatury
   * (mini-300) i po otwarciu na cały ekran zdjęcie byłoby rozpikselowane. Tutaj mierzymy
   * szerokość viewportową i dobieramy właściwy, większy rozmiar — bez cache widoku podstawowego.
   */
  public recomputeLightboxPreview(root: HTMLElement): void {
    if (!root) {
      return;
    }
    const mq = this.globalService.mediaQuery.value;
    this.getBackgroundsForQuery(mq)
      .filter(item => (item.uId || item.modelId) && !item.isHover)
      .forEach(item => {
        const selectors = [
          `.block_${item.uId}`,
          `.block_${item.modelId}`,
        ].join(', ');

        // Szukamy wyłącznie w poddrzewie widoku powiększonego (wraz z klonami karuzeli).
        const els = root.querySelectorAll<HTMLElement>(selectors);

        els.forEach(el => {
          // 1. nanieś zapisane wcześniej style background-*
          Object.entries(item.backgroundStyles).forEach(([prop, value]: [string, string]) => {
            el.style.setProperty(prop, value);
          });

          // 2. dobierz obraz wg realnej szerokości podglądu (viewport), bez cache basic-view.
          // Nie modyfikujemy item.backgroundImage, by nie nadpisać bazowego URL rozmiarem podglądu.
          const width = Math.round(el.getBoundingClientRect().width);
          if (!width) {
            return;
          }
          const baseUrl = item.backgroundImage.split('?')[0];
          const newUrl = this.getImageUrl(baseUrl, width.toString());
          StyleService.setBackgroundImage(el, item.backgroundImageProperty, newUrl);
        });
      });
  }

  /**
   * Obraz tła nanosimy albo wprost na background-image bloku, albo na zmienną, z której czyta
   * go pseudoelement ::before (bloki z animacją tła). Zmienna nie potrzebuje `!important` —
   * zapis inline i tak wygrywa z arkuszem, a `!important` na niej blokowałby nadpisanie
   * z węższego breakpointa.
   */
  private static setBackgroundImage(el: HTMLElement, property: string, url: string): void {
    const priority = property === PSEUDO_BACKGROUND_IMAGE_VAR ? '' : 'important';
    el.style.setProperty(property, `url("${url}")`, priority);
  }

  private getImageUrl(url: string, size?: string): string {

    const screenWidth = size ? parseInt(size, 10) : innerWidth;
    const width = getImgSizeByContainerSize(screenWidth);
    if (url.includes('storage/media')) {
      const glue = url.includes('?') ? '&' : '?';
      // Jeżeli mamy width, doklejamy ją do URL
      return `${url}${glue}size=${width}`;
    }
    const ignoredExtensions = ['svg', 'gif'];
    const extension = url.split('.').pop();
    if (ignoredExtensions.includes(extension)) {
      return url;
    }
    if (url.endsWith('frame_1.jpg')) {
      return url;
    }
    const urlParts = url.split('/').filter(part => !part.includes('mini-'));
    const filename = urlParts[urlParts.length - 1];
    return `${urlParts.slice(0, -1).join('/')}/mini-${width}/${filename}`;

  }

  private removeStyleSection(head: HTMLHeadElement, sectionName: string): void {
    this.getStyleSection(head, sectionName)?.remove();
  }

  private getStyleSection(head: HTMLHeadElement, sectionName: string): HTMLStyleElement {
    return head.querySelector(`[data-id="${sectionName}"]`);
  }

  private creteStyleSection(sectionName: string, stylesString: string): HTMLStyleElement {
    const style = this.document.createElement('style');
    style.dataset.id = sectionName;
    style.appendChild(this.document.createTextNode(stylesString));

    return style;
  }
}
